const toNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const average = (rows, selector) => {
  const values = rows.map(selector).map(toNumber).filter((value) => Number.isFinite(value));
  if (!values.length) return 0;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
};

const formatOne = (value) => toNumber(value).toFixed(1);
const formatTwo = (value) => toNumber(value).toFixed(2);
const formatPercent = (value) => `${Math.round(toNumber(value))}%`;

const findBest = (rows, metric, direction = 'high') => {
  const validRows = rows.filter((row) => Number.isFinite(Number(row?.[metric])));
  if (!validRows.length) return null;
  return validRows.reduce((best, row) => {
    if (!best) return row;
    const rowValue = toNumber(row[metric]);
    const bestValue = toNumber(best[metric]);
    return direction === 'low'
      ? (rowValue < bestValue ? row : best)
      : (rowValue > bestValue ? row : best);
  }, null);
};

const formatDelta = ({ value, averageValue, kind = 'raw', lowerIsBetter = false, unit = '' }) => {
  const current = toNumber(value);
  const teamAvg = toNumber(averageValue);
  const diff = current - teamAvg;
  if (!Number.isFinite(diff) || Math.abs(diff) < 0.01) return 'Even with team average';

  if (kind === 'percent-points') {
    const amount = Math.abs(diff).toFixed(0);
    return `${diff > 0 ? '+' : '-'}${amount} pts vs team average`;
  }

  if (kind === 'percent-relative' && teamAvg > 0) {
    const pct = Math.abs((diff / teamAvg) * 100).toFixed(1);
    const better = lowerIsBetter ? diff < 0 : diff > 0;
    return `${better ? '+' : '-'}${pct}% ${better ? 'better than' : 'off'} team average`;
  }

  const amount = Math.abs(diff).toFixed(1);
  if (lowerIsBetter) {
    return diff < 0
      ? `${amount}${unit} fewer than team average`
      : `${amount}${unit} above team average`;
  }
  return `${diff > 0 ? '+' : '-'}${amount}${unit} vs team average`;
};

const makeInsight = ({ rows, metric, title, valueFormatter, deltaKind, lowerIsBetter = false, unit = '', direction = 'high' }) => {
  const best = findBest(rows, metric, direction);
  if (!best) return null;
  const value = toNumber(best[metric]);
  if (!Number.isFinite(value) || value <= 0) return null;
  const teamAverage = average(rows, (row) => row[metric]);

  return {
    title,
    player: best.Player,
    value: valueFormatter(value),
    delta: formatDelta({
      value,
      averageValue: teamAverage,
      kind: deltaKind,
      lowerIsBetter,
      unit,
    }),
  };
};

export const buildModeInsights = (mode, modePlayers = []) => {
  const rows = Array.isArray(modePlayers)
    ? modePlayers.filter((row) => row?.Player && toNumber(row.matches) > 0)
    : [];
  if (!rows.length) return [];

  const configs = {
    HP: [
      { metric: 'Impact', title: 'Highest Impact', valueFormatter: formatOne, deltaKind: 'percent-relative' },
      { metric: 'K', title: 'Highest Avg Kills', valueFormatter: (v) => `${formatOne(v)} / match`, unit: ' kills' },
      { metric: 'D', title: 'Lowest Avg Deaths', valueFormatter: (v) => `${formatOne(v)} / match`, lowerIsBetter: true, unit: ' deaths', direction: 'low' },
      { metric: 'winRate', title: 'Best Win Rate', valueFormatter: formatPercent, deltaKind: 'percent-points' },
    ],
    SND: [
      { metric: 'kd', title: 'Best K/D', valueFormatter: formatTwo, unit: ' K/D' },
      { metric: 'adr', title: 'Highest ADR', valueFormatter: formatOne, unit: ' ADR' },
      { metric: 'entryKills', title: 'Most First Kills', valueFormatter: (v) => `${formatOne(v)} / match`, unit: ' first kills' },
      { metric: 'survivability', title: 'Best Survivability', valueFormatter: (v) => formatPercent(v * 100), deltaKind: 'percent-relative' },
    ],
    CTL: [
      { metric: 'Impact', title: 'Highest Impact', valueFormatter: formatOne, deltaKind: 'percent-relative' },
      { metric: 'ctlAdr', title: 'Highest ADR', valueFormatter: formatOne, unit: ' ADR' },
      { metric: 'objectiveScore', title: 'Best Objective Score', valueFormatter: formatOne, unit: ' objective' },
      { metric: 'winRate', title: 'Best Win Rate', valueFormatter: formatPercent, deltaKind: 'percent-points' },
    ],
  };

  return (configs[mode] || [])
    .map((config) => makeInsight({ rows, ...config }))
    .filter(Boolean);
};

const getRowDateValue = (row) => {
  const raw = row?.Date || row?.date || '';
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
};

const summarizeTrendGroup = (rows) => {
  const totals = rows.reduce((acc, row) => ({
    kills: acc.kills + toNumber(row?.K ?? row?.kills),
    deaths: acc.deaths + toNumber(row?.D ?? row?.deaths),
    assists: acc.assists + toNumber(row?.A ?? row?.assists),
    score: acc.score + toNumber(row?.Score ?? row?.score),
  }), { kills: 0, deaths: 0, assists: 0, score: 0 });

  const count = rows.length || 1;
  return {
    kd: totals.deaths > 0 ? totals.kills / totals.deaths : totals.kills,
    kills: totals.kills / count,
    deaths: totals.deaths / count,
    assists: totals.assists / count,
    score: totals.score / count,
  };
};

const getTrendDirection = ({ metric, diff, percent }) => {
  if (Math.abs(percent) < 2) return 'stable';
  if (metric === 'deaths') return diff < 0 ? 'up' : 'down';
  return diff > 0 ? 'up' : 'down';
};

const formatTrendDelta = ({ metric, recent, previous }) => {
  const diff = recent - previous;
  const percent = previous !== 0 ? (diff / Math.abs(previous)) * 100 : 0;
  const direction = getTrendDirection({ metric, diff, percent });

  if (direction === 'stable') return { text: 'Stable', direction };
  if (metric === 'deaths') {
    return {
      text: `${diff < 0 ? '-' : '+'}${Math.abs(percent).toFixed(0)}%`,
      direction,
    };
  }
  return {
    text: `${diff > 0 ? '+' : '-'}${Math.abs(percent).toFixed(0)}%`,
    direction,
  };
};

export const calculateRecentTrend = (rows = []) => {
  const sorted = [...(rows || [])].sort((a, b) => getRowDateValue(b) - getRowDateValue(a));
  if (sorted.length < 6) return null;

  const splitSize = Math.min(10, Math.floor(sorted.length / 2));
  if (splitSize < 3) return null;

  const recentRows = sorted.slice(0, splitSize);
  const previousRows = sorted.slice(splitSize, splitSize * 2);
  if (recentRows.length < 3 || previousRows.length < 3) return null;

  const recent = summarizeTrendGroup(recentRows);
  const previous = summarizeTrendGroup(previousRows);
  const metricConfigs = [
    { key: 'kd', label: 'K/D', formatter: formatTwo },
    { key: 'kills', label: 'Kills', formatter: formatOne },
    { key: 'deaths', label: 'Deaths', formatter: formatOne },
    { key: 'assists', label: 'Assists', formatter: formatOne },
    { key: 'score', label: 'Score', formatter: (value) => String(Math.round(toNumber(value))) },
  ];

  return {
    title: `Last ${recentRows.length} vs previous ${previousRows.length}`,
    items: metricConfigs.map((metric) => ({
      key: metric.key,
      label: metric.label,
      value: metric.formatter(recent[metric.key]),
      ...formatTrendDelta({
        metric: metric.key,
        recent: recent[metric.key],
        previous: previous[metric.key],
      }),
    })),
  };
};

export const getComparisonLeader = (rows, key, lowerIsBetter = false) => {
  const valid = rows.filter((row) => row?.stats && Number.isFinite(Number(row.stats[key])));
  if (!valid.length) return null;

  const sorted = [...valid].sort((a, b) => {
    const aValue = Number(a.stats[key]);
    const bValue = Number(b.stats[key]);
    return lowerIsBetter ? aValue - bValue : bValue - aValue;
  });

  const first = Number(sorted[0].stats[key]);
  const second = sorted[1] ? Number(sorted[1].stats[key]) : null;
  if (second !== null && Math.abs(first - second) < 0.001) return null;
  return sorted[0].name;
};
